import {
  Box,
  VStack,
  Button,
  Text,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerHeader,
  DrawerBody,
  Collapse,
  Icon,
  HStack,
  Tooltip,
} from '@chakra-ui/react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/store/auth'
import { ChevronRight, ChevronDown } from 'lucide-react'
import { renderMenuIcon } from '@/lib/icons/menuIcons'
import { useEffect, useState, useMemo, useCallback, memo, useRef } from 'react'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useSiteSettings } from '@/hooks/useSiteSettings'

interface NavigationItemDef {
  name: string
  href: string
  icon: string
}

export interface SidebarProps {
  items?: NavigationItemDef[]
  isOpen?: boolean
  onClose?: () => void
  /** Desktop collapse state (md+) */
  isCollapsed?: boolean
  /** Alias for isCollapsed (compatibility) */
  collapsed?: boolean
}

const NavIcon = memo(({ icon }: { icon: string }) => (
  <Box display="inline-flex" alignItems="center" justifyContent="center" w="20px" h="20px" flexShrink={0}>
    {renderMenuIcon(icon)}
  </Box>
))
NavIcon.displayName = 'NavIcon'

const SidebarLogo = memo(({ appName, logoSrc, isCollapsed }: { appName: string; logoSrc: string; isCollapsed?: boolean }) => (
  <Box
    pt={isCollapsed ? 2 : 4}
    px={isCollapsed ? 2 : 5}
    pb={isCollapsed ? 1 : 4}
    flexShrink={0}
    bg="var(--sidebar-bg)"
    position="sticky"
    top={0}
    zIndex={1}
  >
    <HStack justifyContent="center" spacing={2}>
      <img
        src={logoSrc}
        alt={appName || 'Logo'}
        style={{
          width: isCollapsed ? '28px' : '36px',
          maxWidth: isCollapsed ? '28px' : '36px',
          maxHeight: isCollapsed ? '28px' : '36px',
          height: 'auto',
          objectFit: 'contain',
        }}
      />
      {!isCollapsed && appName ? (
        <Text fontWeight="semibold" fontSize="sm" noOfLines={1}>
          {appName}
        </Text>
      ) : null}
    </HStack>
  </Box>
))
SidebarLogo.displayName = 'SidebarLogo'

function SidebarComponent({ items, isOpen = false, onClose, isCollapsed: isCollapsedProp, collapsed: collapsedProp }: SidebarProps) {
  const isCollapsed = isCollapsedProp ?? collapsedProp ?? false
  const location = useLocation()
  const pathname = location.pathname || '/'
  const navigate = useNavigate()
  
  // Only subscribe to user_routes to prevent unnecessary re-renders when other user properties change
  const userRoutes = useAuthStore((state) => state.user?.user_routes)
  const borderColor = useThemeColors().border
  const { settings: siteSettings } = useSiteSettings()
  
  // Initialize openGroups from localStorage to persist across re-renders
  // Use lazy initializer to only read from localStorage once on mount
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    try {
      const stored = localStorage.getItem('sidebar-open-groups')
      if (stored) {
        const parsed = JSON.parse(stored) as Record<string, boolean>
        return parsed
      }
    } catch (e) {
      // Ignore parse errors
    }
    return {}
  })
  
  // Use refs to track manually opened/closed groups that should persist across navigation
  const manuallyOpenedRef = useRef<Set<string>>(new Set())
  const manuallyClosedRef = useRef<Set<string>>(new Set())
  const isInitializedRef = useRef(false)
  
  // Initialize refs from localStorage on mount (only once)
  useEffect(() => {
    if (!isInitializedRef.current) {
      isInitializedRef.current = true
      Object.keys(openGroups).forEach((key) => {
        if (openGroups[key]) {
          manuallyOpenedRef.current.add(key)
        }
      })
    }
  }, [openGroups])
  
  // Memoize database-driven values to prevent recalculation on every render
  // Extract values directly to prevent object reference changes
  const siteName = siteSettings?.site_name
  const appName = useMemo(() => 
    siteName || import.meta.env.VITE_APP_NAME || 'Ticketing System',
    [siteName]
  )
  const sideNavLogo = siteSettings?.sidenav_logo || '/assets/img/philtower-logo.png'
  
  const sidebarBg = 'var(--sidebar-bg)'
  const sidebarText = 'var(--sidebar-text)'
  const sidebarHoverBg = 'var(--sidebar-hover-bg)'
  const sidebarActiveBg = 'var(--sidebar-active-bg)'
  
  // Memoize helper functions to prevent recreation on every render
  const joinPaths = useCallback((parent: string, child: string) => {
    const a = (parent || '').replace(/\/+$/, '')
    const b = (child || '').replace(/^\/+/, '')
    const joined = `${a}/${b}`
    return joined.startsWith('/') ? joined : `/${joined}`
  }, [])
  
  // Memoize routes to prevent unnecessary recalculations and re-renders
  const topLevelRoutes = useMemo(() => {
    const routes = Array.isArray(userRoutes) ? userRoutes : []
    
    // Debug logging to help diagnose navigation issues
    if (routes.length === 0 && userRoutes) {
      console.warn('[Sidebar] No routes found. userRoutes:', userRoutes)
    } else if (routes.length > 0) {
      console.log('[Sidebar] Total routes:', routes.length, 'Routes:', routes)
    }
    
    const filtered = routes.filter((r: any) => {
      // A parent route is included if its own side_nav is 'true'
      // OR if it has at least one child with side_nav 'true'
      const hasDisplayableChildren = Array.isArray(r.children) && r.children.some((c: any) => c.side_nav === 'true')
      return r.side_nav === 'true' || hasDisplayableChildren
    })
    
    if (filtered.length === 0 && routes.length > 0) {
      console.warn('[Sidebar] No routes with side_nav=true. All routes:', routes.map((r: any) => ({
        name: r.name,
        path: r.path,
        side_nav: r.side_nav
      })))
    }
    
    return filtered
  }, [userRoutes])

  const isActivePath = useCallback((targetPath: string) => {
    const norm = (p: string) => ('/' + (p || '').replace(/^\/+/, '')).replace(/\/+$/, '')
    const cur = norm(pathname)
    const tgt = norm(targetPath)
    return cur === tgt || cur.startsWith(tgt + '/')
  }, [pathname])

  useEffect(() => {
    // Auto-open any parent whose child is active
    // This should only ADD groups that need to be open, never remove manually opened ones
    // But respect manually closed groups (don't auto-open them even if they have active children)
    const groupsToOpen: Record<string, boolean> = {}
    topLevelRoutes.forEach((route: any) => {
      const groupKey = route.path || route.name
      // Skip if this group was manually closed
      if (manuallyClosedRef.current.has(groupKey)) {
        return
      }
      if (Array.isArray(route.children)) {
        const shouldOpen = route.children.some((c: any) => isActivePath(joinPaths(route.path, c.path)))
        if (shouldOpen || isActivePath(route.path)) {
          groupsToOpen[groupKey] = true
        }
      } else if (isActivePath(route.path)) {
        groupsToOpen[groupKey] = true
      }
    })
    
    // Merge with previous state to preserve manually opened groups
    // Only add groups that should be open, never remove manually opened ones
    setOpenGroups((prev) => {
      const next = { ...prev }
      let hasChanges = false
      
      // Add groups that should be open for active paths (unless manually closed)
      Object.keys(groupsToOpen).forEach((key) => {
        if (!next[key] && !manuallyClosedRef.current.has(key)) {
          next[key] = true
          hasChanges = true
        }
      })
      
      // Preserve all manually opened groups (tracked in ref)
      manuallyOpenedRef.current.forEach((key) => {
        if (!next[key]) {
          next[key] = true
          hasChanges = true
        }
      })
      
      // Only update if there are actual changes
      if (!hasChanges) return prev
      return next
    })
  }, [pathname, topLevelRoutes, isActivePath, joinPaths])

  // Helper to check if parent has active child
  const hasActiveChild = useCallback((route: any) => {
    if (!Array.isArray(route.children)) return false
    return route.children.some((c: any) => {
      if (c.side_nav !== 'true') return false
      const childPath = joinPaths(route.path, c.path)
      return isActivePath(childPath)
    })
  }, [isActivePath, joinPaths])

  const collapsedW = '64px'
  const expandedW = '240px'

  const renderNavButtons = useCallback(
    (routes: any[]) =>
      routes.map((route: any) => {
        const displayableChildren = Array.isArray(route.children)
          ? route.children.filter((c: any) => c.side_nav === 'true')
          : []
        const hasDisplayableChildren = displayableChildren.length > 0
        const shouldShowGroupChildren = !isCollapsed && hasDisplayableChildren

        if (route.side_nav === 'true' && !hasDisplayableChildren) {
          const isRouteActive = isActivePath(route.path)
          const btn = (
            <Button
              key={route.path}
              variant="ghost"
              justifyContent={isCollapsed ? 'center' : 'flex-start'}
              alignItems="center"
              px={isCollapsed ? 1.5 : 4}
              py={isCollapsed ? 1.5 : 2}
              minH={isCollapsed ? '36px' : undefined}
              w={isCollapsed ? '100%' : undefined}
              onClick={(e) => {
                e.preventDefault()
                navigate(route.path)
                onClose?.()
              }}
              color={sidebarText}
              _hover={{ bg: sidebarHoverBg }}
              bg={isRouteActive ? sidebarActiveBg : 'transparent'}
              fontWeight={isRouteActive ? 'semibold' : 'normal'}
            >
              {isCollapsed ? (
                <NavIcon icon={route.icon} />
              ) : (
                <HStack spacing={3}>
                  <NavIcon icon={route.icon} />
                  <Text>{route.name}</Text>
                </HStack>
              )}
            </Button>
          )
          return isCollapsed ? (
            <Tooltip key={route.path} label={route.name} placement="right" hasArrow>
              <Box w="100%">{btn}</Box>
            </Tooltip>
          ) : (
            btn
          )
        }

        if (hasDisplayableChildren) {
          const groupKey = route.path || route.name
          const groupIsOpen = !!openGroups[groupKey]
          const isParentActive = isActivePath(route.path) || hasActiveChild(route)

          const parentBtn = (
            <Button
              variant="ghost"
              justifyContent={isCollapsed ? 'center' : 'space-between'}
              alignItems="center"
              px={isCollapsed ? 1.5 : 4}
              py={isCollapsed ? 1.5 : 2}
              minH={isCollapsed ? '36px' : undefined}
              w="100%"
              pr={isCollapsed ? 1.5 : 4}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                if (isCollapsed) {
                  if (route.path) navigate(route.path)
                  onClose?.()
                  return
                }
                setOpenGroups((prev) => {
                  const newState = !prev[groupKey]
                  if (newState) {
                    manuallyClosedRef.current.delete(groupKey)
                    manuallyOpenedRef.current.add(groupKey)
                    const updated = { [groupKey]: true }
                    try {
                      localStorage.setItem('sidebar-open-groups', JSON.stringify(updated))
                    } catch {
                      //
                    }
                    return updated
                  }
                  manuallyOpenedRef.current.delete(groupKey)
                  manuallyClosedRef.current.add(groupKey)
                  const updated = { ...prev, [groupKey]: false }
                  try {
                    localStorage.setItem('sidebar-open-groups', JSON.stringify(updated))
                  } catch {
                    //
                  }
                  return updated
                })
              }}
              color={sidebarText}
              _hover={{ bg: sidebarHoverBg }}
              bg={isParentActive ? sidebarActiveBg : 'transparent'}
              fontWeight={isParentActive ? 'semibold' : 'normal'}
            >
              {isCollapsed ? (
                <NavIcon icon={route.icon} />
              ) : (
                <HStack w="full" justify="space-between" spacing={3}>
                  <HStack spacing={3}>
                    <NavIcon icon={route.icon} />
                    <Text>{route.name}</Text>
                  </HStack>
                  <Icon as={groupIsOpen ? ChevronDown : ChevronRight} />
                </HStack>
              )}
            </Button>
          )

          return (
            <Box key={route.path} w="100%">
              {isCollapsed ? (
                <Tooltip label={route.name} placement="right" hasArrow>
                  <Box w="100%">{parentBtn}</Box>
                </Tooltip>
              ) : (
                parentBtn
              )}
              {shouldShowGroupChildren ? (
                <Collapse in={groupIsOpen} animateOpacity>
                  <VStack spacing={1} pl={6} align="stretch" mt={1}>
                    {displayableChildren.map((child: any) => {
                      const childPath = joinPaths(route.path, child.path)
                      const isChildActive = isActivePath(childPath)
                      return (
                        <Button
                          key={child.path}
                          variant="ghost"
                          justifyContent="flex-start"
                          leftIcon={renderMenuIcon(child.icon)}
                          onClick={(e) => {
                            e.preventDefault()
                            navigate(childPath)
                            onClose?.()
                          }}
                          color={sidebarText}
                          _hover={{ bg: sidebarHoverBg }}
                          bg={isChildActive ? sidebarActiveBg : 'transparent'}
                          fontWeight={isChildActive ? 'semibold' : 'normal'}
                        >
                          {child.name}
                        </Button>
                      )
                    })}
                  </VStack>
                </Collapse>
              ) : null}
            </Box>
          )
        }

        return null
      }),
    [
      openGroups,
      isActivePath,
      hasActiveChild,
      joinPaths,
      navigate,
      sidebarText,
      sidebarHoverBg,
      sidebarActiveBg,
      isCollapsed,
      onClose,
    ],
  )

  const fallbackItems = useMemo(
    () =>
      (items || []).map((it) => {
        const btn = (
          <Button
            variant="ghost"
            justifyContent={isCollapsed ? 'center' : 'flex-start'}
            alignItems="center"
            px={isCollapsed ? 1.5 : 4}
            py={isCollapsed ? 1.5 : 2}
            minH={isCollapsed ? '36px' : undefined}
            w={isCollapsed ? '100%' : undefined}
            leftIcon={!isCollapsed ? renderMenuIcon(it.icon) : undefined}
            onClick={(e) => {
              e.preventDefault()
              navigate(it.href)
              onClose?.()
            }}
            color={sidebarText}
            _hover={{ bg: sidebarHoverBg}}
          >
            {isCollapsed ? <NavIcon icon={it.icon} /> : it.name}
          </Button>
        )
        return isCollapsed ? (
          <Tooltip key={it.href} label={it.name} placement="right" hasArrow>
            <Box w="100%">{btn}</Box>
          </Tooltip>
        ) : (
          <Box key={it.href} w="100%">{btn}</Box>
        )
      }),
    [items, navigate, sidebarText, sidebarHoverBg, isCollapsed, onClose],
  )

  return (
    <>
      {/* Desktop sidebar (push layout) */}
      <Box
        w={{ base: 0, md: isCollapsed ? collapsedW : expandedW }}
        minW={{ base: 0, md: isCollapsed ? collapsedW : expandedW }}
        transition="width 0.2s ease"
        flexShrink={0}
        bg={sidebarBg}
        borderRight="1px"
        borderColor={borderColor}
        h="100vh"
        maxH="100vh"
        display={{ base: 'none', md: 'flex' }}
        flexDirection="column"
        color={sidebarText}
        overflow="hidden"
      >
        <Box flex="1" display="flex" flexDirection="column" overflow="hidden">
          <SidebarLogo appName={appName} logoSrc={sideNavLogo} isCollapsed={isCollapsed} />

          <VStack
            spacing={isCollapsed ? 1 : 2}
            align="stretch"
            p={isCollapsed ? 2 : 4}
            pt={isCollapsed ? 1 : 2}
            pr={isCollapsed ? 1 : 2}
            px={isCollapsed ? 1 : 4}
            flex="1"
            overflowY="auto"
            overflowX="hidden"
            css={{
              '&::-webkit-scrollbar': { width: '6px' },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': {
                background: 'rgba(148, 163, 184, 0.35)',
                borderRadius: '3px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: 'rgba(148, 163, 184, 0.6)',
              },
            }}
          >
            {topLevelRoutes.length > 0 ? renderNavButtons(topLevelRoutes) : fallbackItems}
          </VStack>
        </Box>
      </Box>

      {/* Mobile drawer */}
    <Drawer isOpen={!!isOpen} placement="left" onClose={onClose || (() => {})}>
      <DrawerOverlay />
      <DrawerContent bg={sidebarBg} color={sidebarText}>
        <DrawerCloseButton />
        <DrawerHeader pt={4} pb={3} pr={12}>
          <HStack justifyContent="center" spacing={2}>
            <img
              src={sideNavLogo}
              alt={appName || 'Logo'}
              style={{ width: '36px', maxWidth: '36px', maxHeight: '36px', height: 'auto', objectFit: 'contain' }}
            />
            {appName ? <Text fontWeight="semibold" fontSize="sm" noOfLines={1}>{appName}</Text> : null}
          </HStack>
        </DrawerHeader>
        <DrawerBody>
          <VStack spacing={4} align="stretch">
            {topLevelRoutes.length > 0 ? renderNavButtons(topLevelRoutes) : fallbackItems}
          </VStack>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
    </>
  )
}

// Memoize the component to prevent re-renders when props haven't changed
export const Sidebar = memo(SidebarComponent, (prevProps, nextProps) => {
  // Only re-render if isOpen, onClose, or items actually change
  // Use deep comparison for items array to prevent unnecessary re-renders
  let itemsEqual = prevProps.items === nextProps.items
  if (!itemsEqual && prevProps.items && nextProps.items) {
    const prevItems = prevProps.items
    const nextItems = nextProps.items
    itemsEqual = prevItems.length === nextItems.length &&
      prevItems.every((item, idx) => {
        const nextItem = nextItems[idx]
        return nextItem && item.href === nextItem.href &&
          item.name === nextItem.name &&
          item.icon === nextItem.icon
      })
  }
  
  return (
    prevProps.isOpen === nextProps.isOpen &&
    prevProps.onClose === nextProps.onClose &&
    (prevProps.isCollapsed ?? prevProps.collapsed) === (nextProps.isCollapsed ?? nextProps.collapsed) &&
    itemsEqual
  )
})

export default Sidebar

